/**
 * The small building blocks of the design system: icon, buttons, chips,
 * cards, banners, progress, skeletons, empty states and page headers.
 * Handwritten on purpose — no component library — and styled only through
 * the tokens in styles.css, so dark mode and the brand colour are one place.
 */
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router-dom'
import type { IconName } from './icons.js'

export function cx(...names: (string | false | null | undefined)[]): string {
  return names.filter(Boolean).join(' ')
}

export function Icon({ name, filled, size, className }: { name: IconName; filled?: boolean; size?: 18 | 20 | 24 | 32 | 40; className?: string }) {
  return <span className={cx('icon', filled && 'icon-filled', size && `icon-${size}`, className)} aria-hidden="true">{name}</span>
}

export type ButtonVariant = 'filled' | 'tonal' | 'outlined' | 'text' | 'danger' | 'danger-text'

interface ButtonLook {
  variant?: ButtonVariant
  icon?: IconName
  trailingIcon?: IconName
  small?: boolean
}

function buttonClass(variant: ButtonVariant, small: boolean | undefined, className: string | undefined): string {
  return cx('btn', `btn-${variant}`, small && 'btn-small', className)
}

function ButtonContent({ icon, trailingIcon, children }: { icon?: IconName; trailingIcon?: IconName; children?: ReactNode }) {
  return <>
    {icon && <Icon name={icon} size={18}/>}
    {children !== undefined && children !== null && <span className="btn-label">{children}</span>}
    {trailingIcon && <Icon name={trailingIcon} size={18}/>}
  </>
}

export function Button({ variant = 'outlined', icon, trailingIcon, small, className, type = 'button', children, ...rest }: ButtonLook & ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type={type} className={buttonClass(variant, small, className)} {...rest}>
    <ButtonContent icon={icon} trailingIcon={trailingIcon}>{children}</ButtonContent>
  </button>
}

export function LinkButton({ variant = 'outlined', icon, trailingIcon, small, className, children, ...rest }: ButtonLook & Omit<LinkProps, 'className'> & { className?: string }) {
  return <Link className={buttonClass(variant, small, className)} {...rest}>
    <ButtonContent icon={icon} trailingIcon={trailingIcon}>{children}</ButtonContent>
  </Link>
}

/** A link that leaves the interface: always a new tab, never the opener handed over. */
export function ExternalButton({ variant = 'outlined', icon = 'open_in_new', small, className, children, href, ...rest }: ButtonLook & AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  return <a className={buttonClass(variant, small, className)} href={href} target="_blank" rel="noopener noreferrer" {...rest}>
    <ButtonContent icon={icon}>{children}</ButtonContent>
  </a>
}

export function IconButton({ icon, label, filled, className, type = 'button', selected, ...rest }: { icon: IconName; label: string; filled?: boolean; selected?: boolean } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>) {
  return <button type={type} className={cx('icon-btn', filled && 'icon-btn-filled', selected && 'is-selected', className)} aria-label={label} title={label} {...rest}>
    <Icon name={icon}/>
  </button>
}

export function IconLink({ icon, label, className, ...rest }: { icon: IconName; label: string } & Omit<LinkProps, 'className' | 'children'> & { className?: string }) {
  return <Link className={cx('icon-btn', className)} aria-label={label} title={label} {...rest}><Icon name={icon}/></Link>
}

export function IconExternal({ icon = 'open_in_new', label, href, className }: { icon?: IconName; label: string; href: string; className?: string }) {
  return <a className={cx('icon-btn', className)} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} title={label}><Icon name={icon}/></a>
}

export type ChipTone = 'neutral' | 'draft' | 'review' | 'published' | 'trashed' | 'success' | 'error' | 'warning' | 'info' | 'progress'

export function StatusChip({ tone, children, icon }: { tone: ChipTone; children: ReactNode; icon?: IconName }) {
  return <span className={cx('status-chip', `tone-${tone}`)}>{icon ? <Icon name={icon} size={18}/> : <span className="status-dot" aria-hidden="true"/>}{children}</span>
}

/** A filter chip that is a link: the filter lives in the URL. */
export function FilterChipLink({ selected, children, icon, ...rest }: { selected: boolean; icon?: IconName } & Omit<LinkProps, 'className'>) {
  return <Link className={cx('chip', 'chip-filter', selected && 'is-selected')} aria-current={selected ? 'true' : undefined} {...rest}>
    {selected ? <Icon name="check" size={18}/> : icon ? <Icon name={icon} size={18}/> : null}
    <span>{children}</span>
  </Link>
}

export function FilterChip({ selected, children, icon, ...rest }: { selected: boolean; icon?: IconName } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type="button" className={cx('chip', 'chip-filter', selected && 'is-selected')} aria-pressed={selected} {...rest}>
    {selected ? <Icon name="check" size={18}/> : icon ? <Icon name={icon} size={18}/> : null}
    <span>{children}</span>
  </button>
}

export function Card({ title, subtitle, actions, children, className, icon, as = 'section', id }: {
  title?: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  children?: ReactNode
  className?: string
  icon?: IconName
  as?: 'section' | 'div' | 'article' | 'aside'
  id?: string
}) {
  const Tag = as
  return <Tag className={cx('card', className)} id={id}>
    {(title || actions) && <header className="card-head">
      {icon && <span className="card-icon"><Icon name={icon}/></span>}
      <div className="card-titles">
        {title && <h2 className="card-title">{title}</h2>}
        {subtitle && <p className="card-subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="card-actions">{actions}</div>}
    </header>}
    {children}
  </Tag>
}

export type BannerTone = 'info' | 'success' | 'warning' | 'error'
const BANNER_ICONS: Record<BannerTone, IconName> = { info: 'info', success: 'check_circle', warning: 'warning', error: 'error' }

export function Banner({ tone = 'info', title, children, action, className }: { tone?: BannerTone; title?: ReactNode; children?: ReactNode; action?: ReactNode; className?: string }) {
  return <div className={cx('banner', `banner-${tone}`, className)} role={tone === 'error' ? 'alert' : 'status'}>
    <Icon name={BANNER_ICONS[tone]} className="banner-icon"/>
    <div className="banner-body">
      {title && <strong className="banner-title">{title}</strong>}
      {children && <div className="banner-text">{children}</div>}
    </div>
    {action && <div className="banner-action">{action}</div>}
  </div>
}

export function LinearProgress({ value, max = 1, label }: { value?: number; max?: number; label: string }) {
  const determinate = value !== undefined
  const percent = determinate ? Math.min(100, Math.max(0, (value / Math.max(max, 1)) * 100)) : 0
  return <div
    className={cx('progress', !determinate && 'progress-indeterminate')}
    role="progressbar"
    aria-label={label}
    aria-valuemin={determinate ? 0 : undefined}
    aria-valuemax={determinate ? 100 : undefined}
    aria-valuenow={determinate ? Math.round(percent) : undefined}
  >
    <span className="progress-bar" style={determinate ? { width: `${percent}%` } : undefined}/>
  </div>
}

export function Skeleton({ width, height = 16, className }: { width?: number | string; height?: number; className?: string }) {
  return <span className={cx('skeleton', className)} style={{ width, height }} aria-hidden="true"/>
}

export function SkeletonLines({ count = 3 }: { count?: number }) {
  return <div className="skeleton-lines" aria-hidden="true">
    {Array.from({ length: count }, (_, index) => <Skeleton key={index} width={`${90 - (index % 3) * 18}%`}/>)}
  </div>
}

export function TableSkeleton({ rows = 6, columns = 4, label }: { rows?: number; columns?: number; label: string }) {
  return <div className="table-skeleton" role="status" aria-label={label}>
    {Array.from({ length: rows }, (_, row) => <div className="table-skeleton-row" key={row}>
      {Array.from({ length: columns }, (_, column) => <Skeleton key={column} width={column === 0 ? '60%' : '40%'}/>)}
    </div>)}
  </div>
}

export function PageSkeleton({ label }: { label: string }) {
  return <div className="page-skeleton" role="status" aria-label={label}>
    <Skeleton width={180} height={14}/>
    <Skeleton width="45%" height={32}/>
    <Skeleton width="70%" height={16}/>
    <div className="page-skeleton-cards"><Skeleton height={120}/><Skeleton height={120}/><Skeleton height={120}/></div>
  </div>
}

export function EmptyState({ icon, title, children, action, compact }: { icon: IconName; title: ReactNode; children?: ReactNode; action?: ReactNode; compact?: boolean }) {
  return <div className={cx('empty-state', compact && 'empty-state-compact')}>
    <span className="empty-state-icon"><Icon name={icon} size={32}/></span>
    <h2 className="empty-state-title">{title}</h2>
    {children && <div className="empty-state-text">{children}</div>}
    {action && <div className="empty-state-action">{action}</div>}
  </div>
}

export function PageHeader({ title, subtitle, eyebrow, back, actions, thumb, children }: {
  /** The main image of what is being edited, in a fixed box (components/Thumb). */
  thumb?: ReactNode
  title: ReactNode
  subtitle?: ReactNode
  eyebrow?: ReactNode
  back?: { to: string; label: string }
  actions?: ReactNode
  children?: ReactNode
}) {
  return <header className="page-header">
    <div className="page-header-main">
      {back && <IconLink icon="arrow_back" label={back.label} to={back.to} className="page-back"/>}
      {thumb}
      <div className="page-header-titles">
        {eyebrow && <p className="page-eyebrow">{eyebrow}</p>}
        <h1 className="page-title">{title}</h1>
        {subtitle && <p className="page-subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="page-header-actions">{actions}</div>}
    </div>
    {children}
  </header>
}

/** Label and value pairs, for the facts panels. */
export function Facts({ items }: { items: { label: ReactNode; value: ReactNode; title?: string }[] }) {
  return <dl className="facts">
    {items.map((item, index) => <div className="fact" key={index}>
      <dt>{item.label}</dt>
      <dd title={item.title}>{item.value}</dd>
    </div>)}
  </dl>
}
